/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const ClearChatContext = createContext();

export const useClearChat = () => {
  const context = useContext(ClearChatContext);
  if (!context) {
    throw new Error("useClearChat must be used within a ClearChatProvider");
  }
  return context;
};

const HISTORY_STORAGE_KEY = "tutorChatHistory";
const MAX_HISTORY_ITEMS = 50;

const sanitizeRecentHistory = (history) => {
  if (!Array.isArray(history)) {
    return [];
  }

  return history
    .map((item, index) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      let timestamp = item.timestamp;
      if (typeof timestamp === "string") {
        timestamp = new Date(timestamp).getTime();
      }
      if (!Number.isFinite(timestamp)) {
        timestamp = Date.now() + index;
      }

      const normalized = {
        role: item.role === "assistant" ? "assistant" : "user",
        content:
          typeof item.content === "string" ? item.content : item?.preview || "",
        timestamp,
      };

      if (item.hasImage) {
        normalized.hasImage = true;
        normalized.imageName = item.imageName || "uploaded_image.jpg";
        normalized.imageContext =
          item.imageContext ||
          "User uploaded an image with this message";
      }
      if (item.hasFile) {
        normalized.hasFile = true;
        normalized.fileName = item.fileName || "uploaded_file";
        normalized.fileType = item.fileType || "unknown";
        normalized.fileContext =
          item.fileContext ||
          "User attached a document with this message";
      }
      if (item.hasVoice) {
        normalized.hasVoice = true;
        normalized.voiceContext =
          item.voiceContext || "User recorded a voice message";
      }
      if (item.hasLiveCamera) {
        normalized.hasLiveCamera = true;
        normalized.liveCameraContext =
          item.liveCameraContext ||
          "User used live camera assistance for this message";
      }
      if (item.hasOCR) {
        normalized.hasOCR = true;
        normalized.ocrExtractedText = item.ocrExtractedText || "";
        normalized.ocrConfidence = item.ocrConfidence || 0;
        normalized.ocrSuccess = Boolean(item.ocrSuccess);
        normalized.ocrContext =
          item.ocrContext ||
          "OCR extracted text from uploaded image/document";
      }

      return normalized;
    })
    .filter(Boolean);
};

const buildFallbackRecentHistory = (entry) => {
  if (!entry) {
    return [];
  }

  const now =
    typeof entry.updatedAt === "number" && Number.isFinite(entry.updatedAt)
      ? entry.updatedAt
      : Date.now();

  const fallback = [];

  const title =
    typeof entry.title === "string" && entry.title.trim().length > 0
      ? entry.title.trim()
      : "";
  if (title) {
    fallback.push({
      role: "user",
      content: title,
      timestamp: now - 1500,
    });
  }

  const preview =
    typeof entry.preview === "string" && entry.preview.trim().length > 0
      ? entry.preview.trim()
      : "";
  if (preview) {
    fallback.push({
      role: "assistant",
      content: preview,
      timestamp: now - 500,
    });
  }

  if (fallback.length === 0) {
    fallback.push({
      role: "user",
      content: "Previously saved question",
      timestamp: now - 1500,
    });
    fallback.push({
      role: "assistant",
      content: "Here's the response we shared last time.",
      timestamp: now - 500,
    });
  }

  return fallback;
};

const readStoredHistory = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item) => ({
        id:
          typeof item?.id === "string" || typeof item?.id === "number"
            ? item.id
            : null,
        title: item?.title || "Untitled chat",
        preview: item?.preview || "",
        updatedAt: item?.updatedAt || 0,
        messageCount: Number.isFinite(item?.messageCount)
          ? item.messageCount
          : 0,
        metadata: item?.metadata || null,
        recentHistory: sanitizeRecentHistory(item?.recentHistory),
      }))
      .filter((item) => item.id !== null);
  } catch (error) {
    console.error("Failed to parse stored chat history:", error);
    return [];
  }
};

export const ClearChatProvider = ({ children }) => {
  const [clearChatTrigger, setClearChatTrigger] = useState(0);
  const [hasActiveChat, setHasActiveChat] = useState(false);
  const [chatHistory, setChatHistory] = useState(readStoredHistory);
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState(null);

  // Function to trigger clear chat across all components
  const triggerClearChat = useCallback(() => {
    setClearChatTrigger((prev) => prev + 1);
  }, []);

  // Function to reset the trigger (useful for components that have handled the clear)
  const resetClearChatTrigger = useCallback(() => {
    setClearChatTrigger(0);
  }, []);

  // Function to set chat as active
  const setChatActive = useCallback((isActive) => {
    setHasActiveChat(isActive);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(
        HISTORY_STORAGE_KEY,
        JSON.stringify(chatHistory),
      );
    } catch (error) {
      console.error("Failed to persist chat history:", error);
    }
  }, [chatHistory]);

  const addChatToHistory = useCallback((entry) => {
    if (!entry) {
      return null;
    }

    const normalizedUpdatedAt =
      typeof entry.updatedAt === "number" && Number.isFinite(entry.updatedAt)
        ? entry.updatedAt
        : Date.now();

    const normalizedId =
      entry.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const sanitizedTitle =
      typeof entry.title === "string" && entry.title.trim().length > 0
        ? entry.title.trim()
        : "Untitled chat";

    const sanitizedPreview =
      typeof entry.preview === "string" ? entry.preview.trim() : "";

    const normalizedRecentHistory = sanitizeRecentHistory(entry.recentHistory);

    const normalizedEntry = {
      id: normalizedId,
      title: sanitizedTitle,
      preview: sanitizedPreview,
      updatedAt: normalizedUpdatedAt,
      messageCount: Number.isFinite(entry.messageCount)
        ? entry.messageCount
        : 0,
      ...(entry.metadata ? { metadata: entry.metadata } : {}),
      ...(normalizedRecentHistory.length > 0
        ? { recentHistory: normalizedRecentHistory }
        : {}),
    };

    setChatHistory((prev) => {
      const filtered = prev.filter((item) => item.id !== normalizedId);
      const next = [normalizedEntry, ...filtered];

      return next
        .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
        .slice(0, MAX_HISTORY_ITEMS);
    });

    return normalizedId;
  }, []);

  const removeChatFromHistory = useCallback((id) => {
    if (!id) {
      return;
    }
    setChatHistory((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearChatHistory = useCallback(() => {
    setChatHistory([]);
  }, []);

  const selectHistoryEntry = useCallback((entry) => {
    if (!entry) {
      setSelectedHistoryEntry(null);
      return;
    }
    const sanitizedHistory = sanitizeRecentHistory(entry.recentHistory);

    const normalizedHistory =
      sanitizedHistory.length > 0
        ? sanitizedHistory
        : sanitizeRecentHistory(buildFallbackRecentHistory(entry));

    setSelectedHistoryEntry({
      ...entry,
      recentHistory: normalizedHistory,
    });
  }, []);

  const clearSelectedHistoryEntry = useCallback(() => {
    setSelectedHistoryEntry(null);
  }, []);

  const resetClearChatState = useCallback(() => {
    setClearChatTrigger(0);
    setHasActiveChat(false);
    setChatHistory([]);
    setSelectedHistoryEntry(null);
    try {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(HISTORY_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to clear chat history storage:", error);
    }
  }, []);

  const value = {
    clearChatTrigger,
    triggerClearChat,
    resetClearChatTrigger,
    hasActiveChat,
    setChatActive,
    chatHistory,
    addChatToHistory,
    removeChatFromHistory,
    clearChatHistory,
    selectHistoryEntry,
    selectedHistoryEntry,
    clearSelectedHistoryEntry,
    resetClearChatState,
  };

  return (
    <ClearChatContext.Provider value={value}>
      {children}
    </ClearChatContext.Provider>
  );
};
